import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  ArrowLeft,
  LayoutGrid,
  Play,
  Save,
  Trash2,
  Trophy,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  getEvent,
  getLeague,
  listCommanderPodPlayers,
  listCommanderPods,
  listCommanderRounds,
  listEventParticipants,
  listEventPlayerResults,
  listPlayerConstructs,
  listUsers,
} from "../../graphql/queries";
import {
  createCommanderPod,
  createCommanderPodPlayer,
  createCommanderRound,
  createEventPlayerResult,
  createPlayerConstruct,
  deleteCommanderPod,
  deleteCommanderPodPlayer,
  deleteCommanderRound,
  deleteEventPlayerResult,
  updateCommanderPodPlayer,
  updateCommanderRound,
  updateEvent,
  updateEventParticipant,
} from "../../graphql/mutations";
import {
  CommanderCategory,
  generateCommanderPods,
  getCompetitionRanks,
} from "../../lib/commander";
import {
  calculateCommanderPlacementPoints,
  getLeagueScoring,
} from "../../lib/scoring";
import {
  getPlayerDisplayName,
  getPlayerIdentityInput,
  getPlayerIdentityKey,
} from "../../lib/playerIdentity";

const client = generateClient();

const CommanderRounds: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isWorking, setIsWorking] = useState(false);
  const [keepSamePods, setKeepSamePods] = useState(false);

  const { data: event } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const response = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return response.data.getEvent;
    },
    enabled: !!eventId,
  });
  const { data: league } = useQuery({
    queryKey: ["league", event?.leagueId],
    queryFn: async () => {
      const response = await client.graphql({
        query: getLeague,
        variables: { id: event!.leagueId },
      });
      return response.data.getLeague;
    },
    enabled: !!event?.leagueId,
  });

  const loadList = async (query: string, field: string) => {
    const response: any = await client.graphql({
      query,
      variables: { filter: { eventId: { eq: eventId } }, limit: 1000 },
    });
    return response.data[field].items;
  };

  const { data: participants = [] } = useQuery({
    queryKey: ["eventParticipants", eventId],
    queryFn: () => loadList(listEventParticipants, "listEventParticipants"),
    enabled: !!eventId,
  });
  const { data: rounds = [] } = useQuery({
    queryKey: ["commanderRounds", eventId],
    queryFn: () => loadList(listCommanderRounds, "listCommanderRounds"),
    enabled: !!eventId,
  });
  const { data: pods = [] } = useQuery({
    queryKey: ["commanderPods", eventId],
    queryFn: () => loadList(listCommanderPods, "listCommanderPods"),
    enabled: !!eventId,
  });
  const { data: podPlayers = [] } = useQuery({
    queryKey: ["commanderPodPlayers", eventId],
    queryFn: () =>
      loadList(listCommanderPodPlayers, "listCommanderPodPlayers"),
    enabled: !!eventId,
  });
  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return response.data.listUsers.items;
    },
  });
  const { data: constructs = [] } = useQuery({
    queryKey: ["allPlayerConstructs"],
    queryFn: async () => {
      const response = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return response.data.listPlayerConstructs.items;
    },
  });

  const scoring = getLeagueScoring(league);
  const sortedRounds = [...rounds].sort(
    (a: any, b: any) => a.roundNumber - b.roundNumber
  );
  const playerPoints = useMemo(() => {
    const totals = new Map<string, number>();
    const completedRoundIds = new Set(
      rounds
        .filter((round: any) => round.status === "COMPLETED")
        .map((round: any) => round.id)
    );
    podPlayers
      .filter(
        (player: any) =>
          player.placement && completedRoundIds.has(player.roundId)
      )
      .forEach((player: any) => {
        const identityKey = getPlayerIdentityKey(player);
        if (identityKey) {
          totals.set(
            identityKey,
            (totals.get(identityKey) || 0) + (player.points || 0)
          );
        }
      });
    return totals;
  }, [podPlayers, rounds]);
  const getUser = (userId: string) =>
    users.find((user: any) => user.id === userId);
  const getName = (record: any) =>
    getPlayerDisplayName(record, users, constructs);
  const refresh = () => queryClient.invalidateQueries();

  const generateRound = async () => {
    if (rounds.some((round: any) => round.status !== "COMPLETED")) {
      alert("Complete the current round before generating the next one.");
      return;
    }
    const checkedIn = participants.filter(
      (participant: any) => participant.status === "CHECKED_IN"
    );
    if (
      checkedIn.some(
        (participant: any) =>
          !participant.commanderCategory ||
          !getPlayerIdentityKey(participant)
      )
    ) {
      alert(
        "Every checked-in player needs an identity and Commander category."
      );
      return;
    }

    setIsWorking(true);
    try {
      const previousPods = pods.map((pod: any) => ({
        playerIds: podPlayers
          .filter((player: any) => player.podId === pod.id)
          .map((player: any) => getPlayerIdentityKey(player))
          .filter(Boolean) as string[],
      }));
      let generated;
      if (keepSamePods && rounds.length > 0) {
        const previousRound = [...rounds].sort(
          (a: any, b: any) => b.roundNumber - a.roundNumber
        )[0];
        const previousRoundPods = pods
          .filter((pod: any) => pod.roundId === previousRound.id)
          .sort((a: any, b: any) => a.podNumber - b.podNumber);
        const activePlayerIds = new Set<string>(
          checkedIn
            .map((participant: any) => getPlayerIdentityKey(participant))
            .filter(Boolean) as string[]
        );
        const previousPlayerIds = new Set<string>(
          previousRoundPods.flatMap((pod: any) =>
            podPlayers
              .filter((player: any) => player.podId === pod.id)
              .map((player: any) => getPlayerIdentityKey(player))
              .filter(Boolean)
          )
        );
        if (
          activePlayerIds.size !== previousPlayerIds.size ||
          Array.from(activePlayerIds).some(
            (playerId) => !previousPlayerIds.has(playerId)
          )
        ) {
          throw new Error(
            "The active roster changed. Restore the prior roster or turn off “Keep same pods” to generate new groups."
          );
        }
        generated = previousRoundPods.map((pod: any) => ({
          category: pod.category as CommanderCategory,
          playerIds: podPlayers
            .filter((player: any) => player.podId === pod.id)
            .map((player: any) => getPlayerIdentityKey(player))
            .filter(Boolean) as string[],
        }));
      } else {
        generated = generateCommanderPods(
          checkedIn.map((participant: any) => ({
            userId: getPlayerIdentityKey(participant)!,
            category: participant.commanderCategory as CommanderCategory,
            points:
              playerPoints.get(getPlayerIdentityKey(participant) || "") || 0,
          })),
          previousPods
        );
      }
      const roundNumber =
        Math.max(0, ...rounds.map((round: any) => round.roundNumber)) + 1;
      if (event?.maxRounds && roundNumber > event.maxRounds) {
        throw new Error(`This event is limited to ${event.maxRounds} rounds.`);
      }
      const roundResponse: any = await client.graphql({
        query: createCommanderRound,
        variables: {
          input: {
            eventId: eventId!,
            roundNumber,
            status: "DRAFT" as any,
          },
        },
      });
      const round = roundResponse.data.createCommanderRound;

      for (let index = 0; index < generated.length; index += 1) {
        const generatedPod = generated[index];
        const podResponse: any = await client.graphql({
          query: createCommanderPod,
          variables: {
            input: {
              eventId: eventId!,
              roundId: round.id,
              roundNumber,
              podNumber: index + 1,
              category: generatedPod.category as any,
            },
          },
        });
        for (const playerId of generatedPod.playerIds) {
          const participant = checkedIn.find(
            (entry: any) => getPlayerIdentityKey(entry) === playerId
          );
          await client.graphql({
            query: createCommanderPodPlayer,
            variables: {
              input: {
                eventId: eventId!,
                roundId: round.id,
                podId: podResponse.data.createCommanderPod.id,
                roundNumber,
                ...getPlayerIdentityInput(playerId),
                registeredCategory: participant.commanderCategory,
              },
            } as any,
          });
        }
      }
      await refresh();
    } catch (error: any) {
      alert(error.message || "Unable to generate Commander pods.");
    } finally {
      setIsWorking(false);
    }
  };

  const movePlayer = async (playerId: string, podId: string) => {
    await client.graphql({
      query: updateCommanderPodPlayer,
      variables: { input: { id: playerId, podId } },
    });
    await refresh();
  };

  const publishRound = async (round: any) => {
    const roundPods = pods.filter((pod: any) => pod.roundId === round.id);
    const sizes = roundPods.map(
      (pod: any) =>
        podPlayers.filter((player: any) => player.podId === pod.id).length
    );
    if (sizes.some((size: number) => size < 3 || size > 4)) {
      alert("Every pod must contain three or four players before publishing.");
      return;
    }
    await client.graphql({
      query: updateCommanderRound,
      variables: {
        input: {
          id: round.id,
          status: "PUBLISHED" as any,
          publishedAt: new Date().toISOString(),
        },
      },
    });
    await refresh();
  };

  const setPlacement = async (player: any, placement: number) => {
    await client.graphql({
      query: updateCommanderPodPlayer,
      variables: {
        input: {
          id: player.id,
          placement,
          points: calculateCommanderPlacementPoints(
            placement as 1 | 2 | 3 | 4,
            scoring
          ),
        },
      },
    });
    await refresh();
  };

  const completeRound = async (round: any) => {
    const roundPlayers = podPlayers.filter(
      (player: any) => player.roundId === round.id
    );
    if (roundPlayers.some((player: any) => !player.placement)) {
      alert("Enter a placement for every player before completing the round.");
      return;
    }
    await client.graphql({
      query: updateCommanderRound,
      variables: {
        input: {
          id: round.id,
          status: "COMPLETED" as any,
          completedAt: new Date().toISOString(),
        },
      },
    });
    await refresh();
  };

  const deleteRound = async (round: any) => {
    const isLatestRound =
      sortedRounds[sortedRounds.length - 1]?.id === round.id;
    if (!isLatestRound) {
      alert("Only the latest Commander round can be deleted.");
      return;
    }

    const warning =
      round.status === "COMPLETED"
        ? "This removes all pod placements and points from this round."
        : "This removes every pod and pairing in this round.";
    if (
      !window.confirm(
        `Delete Round ${round.roundNumber}?\n\n${warning} This cannot be undone.`,
      )
    ) {
      return;
    }

    setIsWorking(true);
    try {
      const roundPlayers = podPlayers.filter(
        (player: any) => player.roundId === round.id,
      );
      const roundPods = pods.filter((pod: any) => pod.roundId === round.id);

      for (const player of roundPlayers) {
        await client.graphql({
          query: deleteCommanderPodPlayer,
          variables: { input: { id: player.id } },
        });
      }
      for (const pod of roundPods) {
        await client.graphql({
          query: deleteCommanderPod,
          variables: { input: { id: pod.id } },
        });
      }
      await client.graphql({
        query: deleteCommanderRound,
        variables: { input: { id: round.id } },
      });
      await refresh();
    } catch (error) {
      console.error("Unable to delete Commander round:", error);
      alert("Unable to delete the Commander round. Please try again.");
    } finally {
      setIsWorking(false);
    }
  };

  const dropPlayer = async (participant: any) => {
    if (
      !window.confirm(
        `Drop ${getName(participant)} from future rounds? Completed and published round results will be preserved.`
      )
    ) {
      return;
    }

    setIsWorking(true);
    try {
      const draftRoundIds = new Set(
        rounds
          .filter((round: any) => round.status === "DRAFT")
          .map((round: any) => round.id)
      );
      const participantKey = getPlayerIdentityKey(participant);
      const draftSeats = podPlayers.filter(
        (player: any) =>
          getPlayerIdentityKey(player) === participantKey &&
          draftRoundIds.has(player.roundId)
      );
      for (const seat of draftSeats) {
        await client.graphql({
          query: deleteCommanderPodPlayer,
          variables: { input: { id: seat.id } },
        });
      }
      await client.graphql({
        query: updateEventParticipant,
        variables: {
          input: {
            id: participant.id,
            status: "DROPPED" as any,
          },
        },
      });
      await refresh();
    } finally {
      setIsWorking(false);
    }
  };

  const restorePlayer = async (participant: any) => {
    await client.graphql({
      query: updateEventParticipant,
      variables: {
        input: {
          id: participant.id,
          status: "CHECKED_IN" as any,
        },
      },
    });
    await refresh();
  };

  const getOrCreateConstruct = async (user: any) => {
    const existing = constructs.find(
      (construct: any) => construct.userId === user.id
    );
    if (existing) return existing;
    const response: any = await client.graphql({
      query: createPlayerConstruct,
      variables: {
        input: {
          name: user.displayName,
          normalizedName: user.displayName.toLowerCase().trim(),
          userId: user.id,
          totalEvents: 0,
          totalPoints: 0,
          totalWins: 0,
          totalLosses: 0,
          totalDraws: 0,
        },
      },
    });
    return response.data.createPlayerConstruct;
  };

  const finalizeEvent = async () => {
    if (
      sortedRounds.length === 0 ||
      sortedRounds.some((round: any) => round.status !== "COMPLETED")
    ) {
      alert("Complete every generated round before finalizing the event.");
      return;
    }
    setIsWorking(true);
    try {
      const existingResponse: any = await client.graphql({
        query: listEventPlayerResults,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 1000,
        },
      });
      for (const result of existingResponse.data.listEventPlayerResults.items) {
        await client.graphql({
          query: deleteEventPlayerResult,
          variables: { input: { id: result.id } },
        });
      }

      const summaries = participants
        .map((participant: any) => {
          const identityKey = getPlayerIdentityKey(participant);
          const results = podPlayers.filter(
            (player: any) =>
              getPlayerIdentityKey(player) === identityKey && player.placement
          );
          return {
            userId: participant.userId,
            playerConstructId: participant.playerConstructId,
            points: results.reduce(
              (sum: number, player: any) => sum + (player.points || 0),
              0
            ),
            wins: results.filter((player: any) => player.placement === 1)
              .length,
            podsPlayed: results.length,
          };
        })
        .filter((summary: any) => summary.podsPlayed > 0)
        .sort((a: any, b: any) => b.points - a.points);
      const ranked = getCompetitionRanks<any>(
        summaries,
        (summary: any) => summary.points
      );

      for (const summary of ranked) {
        let construct = summary.playerConstructId
          ? constructs.find(
              (candidate: any) => candidate.id === summary.playerConstructId
            )
          : null;
        if (!construct && summary.userId) {
          const user = getUser(summary.userId);
          if (user) construct = await getOrCreateConstruct(user);
        }
        if (!construct) continue;
        await client.graphql({
          query: createEventPlayerResult,
          variables: {
            input: {
              eventId: eventId!,
              playerConstructId: construct.id,
              rank: summary.rank,
              points: summary.points,
              omw: 0,
              gw: 0,
              ogw: 0,
              wins: summary.wins,
              losses: summary.podsPlayed - summary.wins,
              draws: 0,
            },
          },
        });
      }
      await client.graphql({
        query: updateEvent,
        variables: {
          input: {
            id: eventId!,
            fieldSize: summaries.length,
            isCompleted: true,
          },
        },
      });
      await refresh();
      alert("Commander event finalized and league standings updated.");
    } catch (error) {
      console.error(error);
      alert("Unable to finalize the Commander event.");
    } finally {
      setIsWorking(false);
    }
  };

  if (!event || !league) {
    return <div className="p-6 text-muted-foreground">Loading pods…</div>;
  }
  if (event.format !== "Commander") {
    return <div className="p-6">This event is not a Commander event.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(`/admin/events/${eventId}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Event
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <LayoutGrid className="h-7 w-7 text-primary" />
            Commander Pods
          </h1>
          <p className="text-muted-foreground">
            {event.name} · {participants.filter((p: any) => p.status === "CHECKED_IN").length} checked in
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={keepSamePods}
              onChange={(event) => setKeepSamePods(event.target.checked)}
              disabled={rounds.length === 0}
              className="h-4 w-4 rounded border-white/20 accent-primary"
            />
            Keep same pods
          </label>
          <Button
            onClick={generateRound}
            disabled={isWorking || event.isCompleted}
          >
            <Play className="mr-2 h-4 w-4" />
            Generate Next Round
          </Button>
        </div>
        <Button
          variant="outline"
          onClick={finalizeEvent}
          disabled={isWorking || event.isCompleted}
        >
          <Trophy className="mr-2 h-4 w-4" />
          Finalize Event
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Player status between rounds</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {participants
            .filter((participant: any) =>
              ["CHECKED_IN", "DROPPED"].includes(participant.status)
            )
            .sort((a: any, b: any) =>
              getName(a).localeCompare(getName(b))
            )
            .map((participant: any) => (
              <div
                key={participant.id}
                className="flex items-center justify-between rounded-xl border border-white/10 p-3"
              >
                <div>
                  <p className="font-medium">
                    {getName(participant)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {participant.status === "DROPPED"
                      ? "Dropped from future rounds"
                      : "Active"}
                  </p>
                </div>
                {participant.status === "DROPPED" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => restorePlayer(participant)}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Restore
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={isWorking}
                    onClick={() => dropPlayer(participant)}
                  >
                    <UserMinus className="mr-2 h-4 w-4" />
                    Drop
                  </Button>
                )}
              </div>
            ))}
        </CardContent>
      </Card>

      {sortedRounds.map((round: any) => {
        const roundPods = pods
          .filter((pod: any) => pod.roundId === round.id)
          .sort((a: any, b: any) => a.podNumber - b.podNumber);
        return (
          <Card key={round.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Round {round.roundNumber}</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge>{round.status}</Badge>
                  {round.status === "DRAFT" && (
                    <Button size="sm" onClick={() => publishRound(round)}>
                      Publish Round
                    </Button>
                  )}
                  {round.status === "PUBLISHED" && (
                    <Button size="sm" onClick={() => completeRound(round)}>
                      <Save className="mr-2 h-4 w-4" />
                      Complete Round
                    </Button>
                  )}
                  {sortedRounds[sortedRounds.length - 1]?.id === round.id && (
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={isWorking || event.isCompleted}
                      onClick={() => deleteRound(round)}
                      title={
                        event.isCompleted
                          ? "Finalized events cannot have rounds deleted"
                          : `Delete Round ${round.roundNumber}`
                      }
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Round
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 lg:grid-cols-2">
              {roundPods.map((pod: any) => {
                const members = podPlayers.filter(
                  (player: any) => player.podId === pod.id
                );
                return (
                  <div
                    key={pod.id}
                    className="rounded-xl border border-white/10 p-4"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="font-semibold">Pod {pod.podNumber}</h3>
                      <Badge variant="secondary">
                        {pod.category.toLowerCase()}
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      {members.map((player: any) => (
                        <div
                          key={player.id}
                          className="grid items-center gap-2 sm:grid-cols-[1fr_9rem]"
                        >
                          <div>
                            <p className="font-medium">
                              {getName(player)}
                            </p>
                            {round.status === "DRAFT" && (
                              <Select
                                value={player.podId}
                                onValueChange={(podId) =>
                                  movePlayer(player.id, podId)
                                }
                              >
                                <SelectTrigger className="mt-1 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {roundPods.map((target: any) => (
                                    <SelectItem
                                      key={target.id}
                                      value={target.id}
                                    >
                                      Pod {target.podNumber}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          {round.status !== "DRAFT" && (
                            <Select
                              value={
                                player.placement
                                  ? String(player.placement)
                                  : undefined
                              }
                              onValueChange={(placement) =>
                                setPlacement(player, Number(placement))
                              }
                              disabled={round.status === "COMPLETED"}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Placement" />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from(
                                  { length: members.length },
                                  (_, index) => index + 1
                                ).map((placement) => (
                                  <SelectItem
                                    key={placement}
                                    value={String(placement)}
                                  >
                                    {placement} ·{" "}
                                    {calculateCommanderPlacementPoints(
                                      placement as 1 | 2 | 3 | 4,
                                      scoring
                                    )}{" "}
                                    pts
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}

      {sortedRounds.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Check in players and assign categories, then generate Round 1.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommanderRounds;
